import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  BaseBox,
  BaseTypography,
} from "../../../Common/UI/exports";
import { addTenantsToUser, removeTenantFromUser, fetchUserTenants } from "../../../APIs/userManagementAPI";
import { fetchActiveTenants } from "../../../APIs/tenantManagementAPI";
import { toast } from "react-toastify";

// Helper to extract tenant name/identifier
const getTenantIdentifier = (t) => (typeof t === "string" ? t : t?.name || t?.code || t?.id);

function AttachTenants() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const user = state?.user;

  // All tenants
  const [tenants, setTenants] = useState([]);

  // Assigned tenants as a Set of names for fast toggle
  const [assignedSet, setAssignedSet] = useState(new Set());

  const [initialTenants, setInitialTenants] = useState(new Set());

  const [saving, setSaving] = useState(false);

  // Load all tenants and user's current tenants
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch all available tenants
        const all = await fetchActiveTenants();
        setTenants(Array.isArray(all) ? all : []);

        // Fetch user's current tenants
        if (user?.id) {
          const userTenants = await fetchUserTenants(user.id);
          const tenantList = Array.isArray(userTenants) ? userTenants : [];
          // Extract identifiers (names/codes) from tenant objects
          const tenantIds = tenantList.map(t => getTenantIdentifier(t));
          setAssignedSet(new Set(tenantIds));
          setInitialTenants(new Set(tenantIds));
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load data.");
      }
    };
    loadData();
  }, [user?.id]);

  // Derive assigned list (right table) from tenants + assignedSet
  const assignedList = useMemo(() => {
    return tenants
      .map(getTenantIdentifier)
      .filter((name) => !!name && assignedSet.has(name));
  }, [tenants, assignedSet]);

  // Toggle checkbox (assign/unassign)
  const toggleAssign = (tenantName) => {
    setAssignedSet((prev) => {
      const next = new Set(prev);
      next.has(tenantName) ? next.delete(tenantName) : next.add(tenantName);
      return next;
    });
  };

  const handleCancel = () => {
    if (!hasChanges) return;

    if (window.confirm("Discard unsaved changes?")) {
      toast.info("Changes discarded");
      setAssignedSet(new Set(initialTenants));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Find tenants to add (in assignedSet but not in initialTenants)
      const tenantsToAdd = Array.from(assignedSet).filter(t => !initialTenants.has(t));

      // Find tenants to remove (in initialTenants but not in assignedSet)
      const tenantsToRemove = Array.from(initialTenants).filter(t => !assignedSet.has(t));

      // Add new tenants
      if (tenantsToAdd.length > 0) {
        await addTenantsToUser(user.id, tenantsToAdd);
      }

      // Remove tenants
      if (tenantsToRemove.length > 0) {
        await removeTenantFromUser(user.id, tenantsToRemove);
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      setInitialTenants(new Set(assignedSet));

      toast.success("User tenants updated successfully!");
    } catch (error) {
      console.error("Failed to update tenants:", error);
      toast.error("Failed to save user tenants.");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    initialTenants.size !== assignedSet.size ||
    [...assignedSet].some(r => !initialTenants.has(r));

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <BaseBox
        sx={{
          height: "calc(100vh - 140px)",
          display: "flex",
          flexDirection: "column",
          maxWidth: "1160px",
          mx: "auto",
          mt: 0,
          px: 2,
        }}
      >
        <BaseBox
          sx={{
            mb: 2,
            p: 1.5,
            borderLeft: "4px solid #eb0d24",
            backgroundColor: "#fafafa",
          }}
        >
          <BaseTypography sx={{ fontWeight: 600 }}>
            User: {user?.fullName || user?.username || "Unknown"}
          </BaseTypography>

          <BaseTypography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
            Assign or remove tenants for this user
          </BaseTypography>
        </BaseBox>

        <BaseBox sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <BaseBox
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            {/* Left: All Tenants with checkboxes */}
            <BaseBox
              sx={{
                flex: "1 1 360px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                backgroundColor: "#fff",
                alignSelf: "flex-start",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <table className="roles-table">
                <thead>
                  <tr>
                    <th>All Tenants (check to assign)</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.length ? (
                    tenants.map((tenant) => {
                      const name = getTenantIdentifier(tenant);
                      if (!name) return null;
                      const checked = assignedSet.has(name);
                      return (
                        <tr key={name}>
                          <td title={name}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAssign(name)}
                                style={{ marginRight: 8 }}
                              />
                              {name}
                            </label>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="empty">No tenants.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </BaseBox>

            {/* Right: Assigned Tenants (derived, read-only) */}
            <BaseBox
              sx={{
                flex: "1 1 360px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                backgroundColor: "#fff",
                alignSelf: "flex-start",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <table className="roles-table assigned-roles">
                <thead>
                  <tr>
                    <th>Assigned Tenants</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedList.length ? (
                    assignedList.map((name) => (
                      <tr key={name}>
                        <td title={name}>{name}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="empty">No assigned tenants.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </BaseBox>
          </BaseBox>
          <BaseBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: hasChanges ? "#eb0d24" : "#ccc",
                color: "#fff",
                cursor: hasChanges ? "pointer" : "not-allowed",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>

            <button
              onClick={handleCancel}
              disabled={saving || !hasChanges}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: hasChanges ? "#eb0d24" : "#ccc",
                color: "#fff",
                cursor: hasChanges ? "pointer" : "not-allowed",
                opacity: !hasChanges ? 0.7 : 1,
              }}
            >
              Cancel
            </button>

            <button
              onClick={() => navigate("/user-management")}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: "#666",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Back to Users
            </button>
          </BaseBox>
        </BaseBox>
      </BaseBox>
    </>
  );
}

export default AttachTenants;
